import React, { useRef, useState } from "react";
import { Box, IconButton, Paper, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { chartFileName, downloadChartPng } from "../pqrsd/chartExport";

// The KPI tile and chart card of the Informes pages (PQRSD, Usuarios).

// Small KPI tile. `color` tints the value; subtitle is muted secondary ink.
export function StatCard({ title, value, subtitle, color }) {
  return (
    // border-box: without it the padding and border are added on top of the
    // 100% height, the card outgrows its grid cell and the next row paints
    // over its bottom edge.
    <Paper variant="outlined" sx={{ p: 2, height: "100%", boxSizing: "border-box", borderRadius: 2 }}>
      <Typography variant="body2" color="text.secondary" gutterBottom noWrap>
        {title}
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 700, color: color || "text.primary" }}>
        {value}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </Paper>
  );
}

// Chart card wrapper — title + a fixed-height plot area, and a button that saves
// the plot as a PNG labelled with the title and the period it covers.
export function ChartCard({ title, children, empty, exportInfo }) {
  const theme = useTheme();
  const plotRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  const exportPng = async () => {
    if (!plotRef.current || !exportInfo) return;
    setExporting(true);
    try {
      await downloadChartPng(plotRef.current, {
        title,
        subtitle: exportInfo.subtitle,
        fileName: chartFileName(title, exportInfo.rangeKey),
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
        mutedColor: theme.palette.text.secondary,
        fontFamily: theme.typography.fontFamily,
      });
    } catch (e) {
      console.error("chart export error:", e);
      exportInfo.onError?.(e);
    } finally {
      setExporting(false);
    }
  };

  return (
    // border-box: without it the padding and border are added on top of the
    // 100% height, the card outgrows its grid cell and the next row paints
    // over its bottom edge.
    <Paper variant="outlined" sx={{ p: 2, height: "100%", boxSizing: "border-box", borderRadius: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, flexGrow: 1 }}>
          {title}
        </Typography>
        {exportInfo && !empty && (
          <Tooltip title="Descargar como PNG">
            <span>
              <IconButton
                size="small"
                onClick={exportPng}
                disabled={exporting}
                aria-label={`Descargar "${title}" como PNG`}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Stack>
      {empty ? (
        <Box sx={{ height: 260, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography variant="body2" color="text.secondary">
            Sin datos en el período.
          </Typography>
        </Box>
      ) : (
        <Box ref={plotRef}>{children}</Box>
      )}
    </Paper>
  );
}
